import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';

export interface EmployerInvite {
  id: string;
  hire_org_id: string;
  invite_source_user_id: string;
  candidate_id: string;
  target_hire_job_id: string | null;
  invite_status: string;
  invite_message: string | null;
  sent_at: string | null;
  responded_at: string | null;
  created_at: string;
  updated_at: string;
  expires_at: string | null;
  job_title: string | null;
  org_name: string | null;
  candidate_name: string | null;
  seek_candidate_number: number | null;
}

export function useEmployerInvites() {
  const { profile } = useAuth();
  const { toast } = useToast();
  const qc = useQueryClient();
  const orgId = profile?.org_id;

  const query = useQuery({
    queryKey: ['employer-invites', orgId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('employer_candidate_invites')
        .select('*')
        .eq('hire_org_id', orgId!)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as EmployerInvite[];
    },
    enabled: !!orgId,
  });

  const withdrawMutation = useMutation({
    mutationFn: async (inviteId: string) => {
      const { data, error } = await supabase.functions.invoke('withdraw-invite', {
        body: { invite_id: inviteId },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      return data;
    },
    onSuccess: () => {
      toast({ title: 'Invitation withdrawn successfully' });
      qc.invalidateQueries({ queryKey: ['employer-invites'] });
      qc.invalidateQueries({ queryKey: ['talent-search'] });
    },
    onError: (e: Error) => {
      toast({ title: 'Failed to withdraw', description: e.message, variant: 'destructive' });
    },
  });

  const invites = query.data ?? [];
  const stats = {
    total: invites.length,
    pending: invites.filter(i => i.invite_status === 'pending' || i.invite_status === 're-invited').length,
    accepted: invites.filter(i => i.invite_status === 'accepted').length,
    declined: invites.filter(i => i.invite_status === 'declined').length,
    withdrawn: invites.filter(i => i.invite_status === 'withdrawn').length,
    expired: invites.filter(i => i.invite_status === 'expired').length,
  };

  return {
    invites,
    isLoading: query.isLoading,
    stats,
    withdraw: withdrawMutation.mutate,
    isWithdrawing: withdrawMutation.isPending,
  };
}
